import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Loader2,
  Search,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { formatCreditReason } from "@/components/admin/admin-user-detail-modal";
import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@super-resume-new/ui/components/card";
import { Input } from "@super-resume-new/ui/components/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/credits")({
  head: () =>
    seo({
      title: "Credit Ledger & Allocation | JobCues Admin",
      description: "Grant rewrite credits, inspect audit logs, and browse global ledger activity.",
      path: "/admin/credits",
      noindex: true,
    }),
  component: AdminCreditsPage,
});

function AdminCreditsPage() {
  const queryClient = useQueryClient();
  const [targetUserId, setTargetUserId] = useState("");
  const [creditAmount, setCreditAmount] = useState(25);
  const [creditReason, setCreditReason] = useState("manual_admin_topup");

  // Ledger filter states
  const [ledgerUserId, setLedgerUserId] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  // Global paginated credit ledger query
  const {
    data: ledgerData,
    isLoading: isLedgerLoading,
    refetch: refetchLedger,
  } = useQuery({
    queryKey: ["admin", "credit-ledger", page, ledgerUserId, reasonFilter],
    queryFn: () =>
      client.admin.listCreditLedger({
        page,
        limit: pageSize,
        userId: ledgerUserId.trim() || undefined,
        reason: reasonFilter,
      }),
  });

  // User credit history for specific user when allocating
  const { data: userCreditData, refetch: refetchUserHistory } = useQuery({
    queryKey: ["admin", "user-credits", targetUserId],
    queryFn: () => client.admin.getUserCreditHistory({ userId: targetUserId }),
    enabled: targetUserId.trim().length > 0,
  });

  const grantMutation = useMutation({
    mutationFn: async () => {
      return client.admin.grantUserCredits({
        userId: targetUserId.trim(),
        amount: creditAmount,
        reason: creditReason,
      });
    },
    onSuccess: () => {
      toast.success(`Granted ${creditAmount} credits to user`);
      refetchUserHistory();
      refetchLedger();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => toast.error(err.message || "Failed to grant credits"),
  });

  const totalLedger = ledgerData?.total ?? 0;
  const totalPages = ledgerData?.totalPages ?? 1;

  return (
    <div className="space-y-6">
      {/* Allocation & Single User Inspector */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-border bg-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Coins className="size-4 text-amber-500" />
              Manual Credit Allocation
            </CardTitle>
            <CardDescription className="text-xs">
              Directly credit candidate balances for tests, refunds, or customer support goodwill.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-foreground">Target User ID</label>
              <Input
                placeholder="User ID string…"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-foreground">Credit Delta</label>
              <Input
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(parseInt(e.target.value, 10) || 0)}
                className="mt-1 text-xs"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Enter positive integer for grants.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold text-foreground">Ledger Reason</label>
              <select
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-foreground font-medium outline-none"
              >
                <option value="manual_admin_topup">Manual Admin Topup</option>
                <option value="support_goodwill">Customer Support Goodwill</option>
                <option value="refund">Refund Compensation</option>
                <option value="testing_grant">Developer Verification Grant</option>
              </select>
            </div>
          </CardContent>
          <CardFooter className="border-t border-border pt-4">
            <Button
              className="w-full font-semibold"
              disabled={!targetUserId || grantMutation.isPending}
              onClick={() => grantMutation.mutate()}
            >
              {grantMutation.isPending ? "Allocating…" : "Execute Credit Grant"}
            </Button>
          </CardFooter>
        </Card>

        <Card className="lg:col-span-2 border-border bg-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base font-semibold">Target User Ledger Preview</CardTitle>
              <CardDescription className="text-xs">
                Audited ledger entries for target user ID above.
              </CardDescription>
            </div>
            {userCreditData && (
              <Badge
                variant="outline"
                className="border-amber-500/30 text-amber-500 bg-amber-500/10 font-semibold"
              >
                Current Balance: {userCreditData.balance}
              </Badge>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Delta</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Ref ID</TableHead>
                  <TableHead className="text-right">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!targetUserId ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-12 text-center text-muted-foreground text-xs"
                    >
                      Enter a User ID on the left to inspect their balance and recent ledger history.
                    </TableCell>
                  </TableRow>
                ) : userCreditData?.entries?.length ? (
                  userCreditData.entries.map((e: any) => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <span
                          className={`font-semibold tabular-nums ${
                            e.delta > 0 ? "text-emerald-500" : "text-destructive"
                          }`}
                        >
                          {e.delta > 0 ? `+${e.delta}` : e.delta}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {formatCreditReason(e.reason)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">
                        {e.ref || "—"}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                        {e.createdAt ? new Date(e.createdAt).toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-8 text-center text-muted-foreground text-xs"
                    >
                      No ledger entries found for this user.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Global Paginated Credit Ledger */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold">Global Credit Ledger</CardTitle>
            <CardDescription className="text-xs">
              Complete chronological audit trail of all credit movements across the platform.
            </CardDescription>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative w-48 sm:w-60">
              <Search className="absolute left-2.5 top-2.5 size-3.5 text-muted-foreground" />
              <Input
                placeholder="Filter by User ID…"
                value={ledgerUserId}
                onChange={(e) => {
                  setLedgerUserId(e.target.value);
                  setPage(1);
                }}
                className="pl-8 text-xs h-8"
              />
            </div>

            <select
              value={reasonFilter}
              onChange={(e) => {
                setReasonFilter(e.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground font-medium outline-none"
            >
              <option value="all">All Reasons</option>
              <option value="signup_grant">Signup Grant</option>
              <option value="purchase">Purchase</option>
              <option value="rewrite_debit">Rewrite Debit</option>
              <option value="ats_booster_debit">ATS Booster Debit</option>
              <option value="admin_grant">Admin Grant</option>
              <option value="manual_admin_topup">Manual Topup</option>
              <option value="refund">Refund</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User Account</TableHead>
                <TableHead>Delta</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Reference ID</TableHead>
                <TableHead className="text-right">Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLedgerLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                    <Loader2 className="mx-auto size-6 animate-spin text-brand mb-2" />
                    Loading credit ledger…
                  </TableCell>
                </TableRow>
              ) : ledgerData?.entries?.length ? (
                ledgerData.entries.map((e: any) => (
                  <TableRow key={e.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-xs text-foreground">
                          {e.userName || "User"}
                        </span>
                        <span className="text-[11px] text-muted-foreground font-mono">
                          {e.userEmail || e.userId}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          e.delta > 0
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 font-mono font-semibold"
                            : "bg-destructive/10 text-destructive border-destructive/30 font-mono font-semibold"
                        }
                      >
                        {e.delta > 0 ? `+${e.delta}` : e.delta} cr
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="font-medium text-foreground">{formatCreditReason(e.reason)}</span>
                      <span className="block text-[10px] font-mono text-muted-foreground">{e.reason}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono truncate max-w-[200px]" title={e.ref || ""}>
                      {e.ref || "—"}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                      {e.createdAt ? new Date(e.createdAt).toLocaleString() : "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground text-xs">
                    No ledger transactions matching the filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="size-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages} ({totalLedger} total transactions)
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
